import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { GlobalEntityType, RsWorkspaceEntityType } from "shared/types";
import { isNotEmpty } from "utils/validate";
import { ErrorType } from "api/api-error";
import { usePrevious } from "hooks/usePrevious";
import RenamableCore from "../RenamableCore";
import { isInProgressSelector, serverErrorsSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { setServerError } from "store/knowledge-base-item/knowledge-base-item.actions";
import { setRenamingProgress } from "store/global/global.actions";
import * as styles from "./styles.module.scss";
const getRequestTemplate = (entityType) => {
    switch (entityType) {
        case GlobalEntityType.Recipe:
            return RequestTemplate.RECIPE_PUT;
        case GlobalEntityType.CellModel:
            return RequestTemplate.CELL_MODEL_PUT;
        case GlobalEntityType.RsWorkspace:
            return RequestTemplate.WORKSPACE_PUT;
        case RsWorkspaceEntityType.ResultSet:
            return RequestTemplate.RESULT_SET_PUT;
        case RsWorkspaceEntityType.ResultSetElement:
            return RequestTemplate.RESULT_SET_ELEMENT_PUT;
        case RsWorkspaceEntityType.TabularView:
            return [
                RequestTemplate.TABLE_ELEMENT_PUT,
                RequestTemplate.TABLE_ELEMENT_POST,
                RequestTemplate.RESULT_SET_ELEMENT_DUPLICATE,
            ];
        case RsWorkspaceEntityType.SpaceViewer:
            return [
                RequestTemplate.SPACEVIEWER_ELEMENT_PUT,
                RequestTemplate.SPACEVIEWER_ELEMENT_POST,
                RequestTemplate.RESULT_SET_ELEMENT_DUPLICATE,
            ];
        case RsWorkspaceEntityType.ProcessDataViewer:
            return [
                RequestTemplate.PROCESS_DATA_VIEWER_ELEMENT_PUT,
                RequestTemplate.PROCESS_DATA_VIEWER_ELEMENT_POST,
                RequestTemplate.RESULT_SET_ELEMENT_DUPLICATE,
            ];
        case RsWorkspaceEntityType.LineGraph:
            return [
                RequestTemplate.LINE_GRAPH_ELEMENT_PUT,
                RequestTemplate.LINE_GRAPH_ELEMENT_POST,
                RequestTemplate.RESULT_SET_ELEMENT_DUPLICATE,
            ];
        case RsWorkspaceEntityType.Simulation:
            return [RequestTemplate.SIMULATION_POST, RequestTemplate.SIMULATION_PUT];
        case RsWorkspaceEntityType.Insight:
            return [RequestTemplate.INSIGHT_POST, RequestTemplate.INSIGHT_PUT];
        case RsWorkspaceEntityType.Optimization:
            return [RequestTemplate.OPTIMIZATION_POST, RequestTemplate.OPTIMIZATION_PUT];
        case RsWorkspaceEntityType.CustomVariable:
            return [RequestTemplate.CUSTOM_VARIABLE_POST, RequestTemplate.CUSTOM_VARIABLE_PUT];
    }
};
const RsRenamable = ({ classes, text, entityId, resetErrorOnPreviousEqual, ...restProps }) => {
    const dispatch = useDispatch();
    const serverErrors = useSelector(serverErrorsSelector(getRequestTemplate(restProps.entityType), undefined, entityId));
    const communicationIsInProgress = useSelector(isInProgressSelector);
    const prevText = usePrevious(text);
    const getExternalError = useCallback(() => {
        if (!communicationIsInProgress && serverErrors !== null && serverErrors[0]) {
            const { text, errorType } = serverErrors[0];
            if (errorType === ErrorType.Validation) {
                return text;
            }
        }
        return false;
    }, [communicationIsInProgress, serverErrors]);
    const onClickOutside = useCallback((hasError, isOutside, value) => {
        if (resetErrorOnPreviousEqual && isNotEmpty(serverErrors) && text === value && isOutside) {
            dispatch(setServerError(null));
            dispatch(setRenamingProgress(false));
        }
    }, [dispatch, resetErrorOnPreviousEqual, serverErrors, text]);
    useEffect(() => {
        if (serverErrors !== null && text !== prevText) {
            dispatch(setServerError(null));
            dispatch(setRenamingProgress(false));
        }
    }, [dispatch, prevText, serverErrors, text]);
    return (React.createElement(RenamableCore, { ...restProps, text: text, classes: {
            ...classes,
            root: classNames(classes?.root, styles["rs-renamable"]),
            container: classNames(classes?.container, styles["container"]),
            tooltipPortal: classNames(classes?.tooltipPortal, styles["tooltip-portal"]),
        }, http: true, getExternalError: getExternalError, clickOutside: (...args) => {
            onClickOutside(...args);
            restProps?.clickOutside && restProps?.clickOutside(...args);
        } }));
};
export default RsRenamable;
